import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Dialog from '../../../../../../common/element/Dialog';
import BtnPrimary from '../../../../../../common/element/Buttons/btnPrimary';
import Select from '../../../../../../common/element/Select';
import SweetAlertService from '../../../../../../common/SweetAlert/SweetAlert';
import { getCoachListAll, assignCoach, clearAssignCoach } from '../../../../../../redux/Coach/coachSlice';
import {
  selectCoachListAll,
  selectCoachListAllLoading,
  selectAssignCoachLoading,
  selectAssignCoachData,
  selectAssignCoachError
} from '../../../../../../redux/Coach/coachSelector';

const AssignCoachDialog = ({
  dialogOpen,
  onDialogClose,
  onSuccess, // Callback after successful assignment
  mode = 'add', // 'add' or 'edit'
  currentCoach = null, // Current coach data for edit mode
  fetchCoaches
}) => {
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(useLocation().search);
  const companyId = searchParams.get('companyId');

  const [selectedCoach, setSelectedCoach] = useState([]);

  // Get data from Redux store
  const coachListData = useSelector(selectCoachListAll);
  const coachListLoading = useSelector(selectCoachListAllLoading);
  const assignCoachLoading = useSelector(selectAssignCoachLoading);
  const assignCoachData = useSelector(selectAssignCoachData);
  const assignCoachError = useSelector(selectAssignCoachError);

  // Fetch coaches when dialog opens
  useEffect(() => {
    if (dialogOpen && companyId) {
      dispatch(getCoachListAll({ excludeCompany: companyId }));
    }
  }, [dialogOpen, companyId, dispatch]);

  // Initialize selected coach based on mode
  useEffect(() => {
    if (mode === 'edit' && currentCoach) {
      // Ensure currentCoach is an array
      setSelectedCoach(Array.isArray(currentCoach) ? currentCoach : [currentCoach]);
    } else {
      setSelectedCoach([]);
    }
  }, [mode, currentCoach, dialogOpen]);

  // Handle successful assignment
  useEffect(() => {
    if (assignCoachData && !assignCoachLoading) {
      // Show success message
      setTimeout(() => {
        SweetAlertService.showSuccess({
          title: 'Success!',
          message: assignCoachData?.message || 'Coaches assigned successfully',
          onConfirm: () => {
            // Clear assignment state
            dispatch(clearAssignCoach());

            // Call onSuccess callback if provided
            if (onSuccess) {
              onSuccess(assignCoachData);
            }

            // Refresh coaches list
            if (fetchCoaches) {
              fetchCoaches();
            }

            // Close dialog
            handleClose();
          }
        });
      }, 200);
    }
  }, [assignCoachData, assignCoachLoading]);

  // Handle assignment error
  useEffect(() => {
    if (assignCoachError) {
      // Show error alert
      SweetAlertService.showError({
        title: 'Error!',
        message: assignCoachError || 'Failed to assign coaches',
      });
    }
  }, [assignCoachError]);

  const handleSelectChange = (value) => {
    setSelectedCoach(value);
  };

  const handleClose = () => {
    setSelectedCoach([]);
    dispatch(clearAssignCoach());
    onDialogClose();
  };

  const handleAssignCoach = async () => {
    if (!selectedCoach || !companyId) {
      console.error('Missing required data');
      return;
    }

    // selectedCoach can be a single value or array depending on isMulti
    const coachIds = Array.isArray(selectedCoach)
      ? selectedCoach.map(coach => coach.value || coach)
      : [selectedCoach.value || selectedCoach];

    // Dispatch the assignCoach action
    dispatch(assignCoach({
      companyId,
      coachIds
    }));
  };

  // Transform API data to Select component format
  const coachList = coachListData.map(coach => ({
    value: coach.coach_id || coach.id,
    label: coach.name || `${coach.firstName} ${coach.lastName}`.trim()
  }));

  const isEditMode = mode === 'edit';
  const dialogTitle = isEditMode ? 'Edit Coach' : 'Assign Coach';
  const buttonText = isEditMode ? 'Update Coach' : 'Assign Coach';

  // Get current coach label for display
  const getCurrentCoachLabel = () => {
    const coach = coachList.find(c => c.value === currentCoach);
    return coach?.label || 'Unknown Coach';
  };

  // Determine if button should be disabled
  const isButtonDisabled = !selectedCoach || selectedCoach.length === 0 || coachListLoading || assignCoachLoading;

  return (
    <Dialog
      isOpen={dialogOpen}
      onClose={handleClose}
      width={800}
      bodyOpenClassName="overflow-hidden"
    >
      <div
        className="bg-white rounded-lg shadow-lg w-[800px] max-w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex border-b pb-4 px-6 pt-6 items-center justify-between">
          <div className="flex items-center">
            <span className="h-[40px] w-10 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-3 bg-gray-50">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.6603 21.1876C20.1934 19.8611 18.6575 18.8405 17.2952 18.2041C16.9129 17.8479 16.4338 17.5912 15.9248 17.3907C15.6256 17.273 15.4743 16.9354 15.5913 16.6358C15.7925 16.1216 16.0828 15.6577 16.4718 15.2374C16.9226 14.7504 17.252 14.029 17.4242 13.6001C17.5047 13.3997 17.679 13.2476 17.8922 13.2137C18.7298 13.08 19.3267 12.4927 19.5264 11.6245C19.7742 10.5477 19.4929 9.38068 18.4615 8.9328C18.2991 8.86238 18.1947 8.70141 18.2111 8.52479L18.2152 8.48083C18.2301 8.3165 18.1757 8.1615 18.0803 8.03593C18.2204 7.82987 18.1441 7.45316 18.1332 7.21656C18.1213 6.95275 18.1016 6.68894 18.0718 6.42662C18.0207 5.97576 17.9414 5.52751 17.824 5.08895C17.6164 4.31317 17.2874 3.56385 16.7948 2.92594C15.794 1.63037 14.2398 1.03196 12.6432 0.919431C12.3548 0.431311 11.6036 0.433546 11.3178 0.92353C8.92939 1.12921 6.97169 2.53209 6.22982 4.8542C6.07817 5.32854 5.97607 5.81815 5.91086 6.31148C5.87658 6.57231 5.85236 6.83463 5.83709 7.09732C5.82181 7.35852 5.72419 7.83174 5.89745 8.05456C5.81138 8.17678 5.76331 8.3247 5.77747 8.48045L5.78194 8.52777C5.79796 8.70365 5.69475 8.86387 5.53341 8.93504C4.51245 9.38552 4.23337 10.5574 4.48935 11.6201C4.69243 12.464 5.29047 13.0457 6.10835 13.1981C6.30993 13.2357 6.47351 13.3803 6.55548 13.5685C6.79395 14.1154 7.28729 14.9467 7.61817 15.3257C7.95948 15.7162 8.23447 16.1439 8.41779 16.6179C8.53293 16.9156 8.37792 17.2521 8.07983 17.3665C7.51607 17.583 6.98585 17.8654 6.57784 18.2719C5.45069 18.8159 4.25312 19.5578 3.60068 20.6626C3.14311 21.4373 2.90911 22.5447 3.03282 23.4453L3.77059 23.3443C3.71469 22.7284 3.83095 22.083 4.0329 21.5028C4.34105 20.6168 5.11459 19.9919 5.88404 19.5068C5.53676 20.951 6.18101 22.5026 7.66623 22.9348C9.17605 23.3741 10.6039 22.5793 11.2943 21.2636C11.3681 21.1228 11.5183 21.0374 11.677 21.0505C11.8767 21.0673 12.0765 21.0687 12.2765 21.0557C12.4319 21.0456 12.578 21.1302 12.6507 21.2681C13.3493 22.5976 14.8081 23.3894 16.3253 22.9202C17.8009 22.4642 18.4183 20.885 18.0434 19.4531C18.8185 19.9297 19.6304 20.5527 19.9579 21.4343C20.1755 22.0196 20.313 22.7153 20.2433 23.3439L20.9811 23.4449C20.986 23.4061 21.1085 22.4619 20.6603 21.1876C21.1085 22.4619 20.1934 19.8611 20.6603 21.1876ZM9.41788 17.985C9.41788 17.7432 9.39776 17.5036 9.35975 17.2677C9.31653 17.0017 9.574 16.7915 9.82291 16.8948C10.2786 17.0837 10.7611 17.2159 11.2623 17.2823C11.4031 17.3009 11.5064 17.4242 11.4903 17.5655C11.4017 18.3382 11.2549 19.1274 11.0462 19.835C10.9955 20.0068 10.8066 20.1003 10.643 20.0273C10.2332 19.8443 9.86203 19.582 9.55276 19.2579C9.45253 19.1531 9.39738 19.0138 9.40186 18.8688C9.41229 18.5532 9.41788 17.985 9.41788 17.985ZM11.5671 14.7981C11.6375 14.8271 11.7143 14.8435 11.7948 14.8435H12.1469C12.2274 14.8435 12.3041 14.8271 12.3745 14.7981V15.0239C12.3745 15.5433 11.5671 15.5466 11.5671 15.0239V14.7981ZM14.5946 17.985C14.5946 17.985 14.6035 18.56 14.6184 18.8815C14.6251 19.0309 14.5677 19.1751 14.4634 19.2824C14.1351 19.6189 13.739 19.8861 13.3027 20.0649C13.1399 20.1316 12.9569 20.0388 12.9063 19.8701C12.6924 19.155 12.5426 18.3543 12.4524 17.5699C12.4364 17.4298 12.5404 17.3072 12.6805 17.2912C13.2051 17.2301 13.71 17.0975 14.1858 16.9007C14.4362 16.7975 14.6952 17.0058 14.6519 17.2733C14.6147 17.5077 14.5949 17.7454 14.5946 17.985ZM11.9814 5.06361C13.8356 5.06361 15.1188 5.71419 15.9449 6.37259C16.0399 6.44823 15.9576 6.5969 15.8428 6.55778C13.3221 5.69519 10.6803 5.68997 8.15622 6.54548C8.04183 6.58424 7.96022 6.43594 8.05449 6.3603C8.87051 5.70636 10.1389 5.06361 11.9814 5.06361ZM5.1526 11.0477C5.07621 10.0961 5.59265 9.49545 6.5484 9.47532C6.58939 9.48389 6.61994 9.48352 6.66205 9.47905C7.0354 9.52041 7.38156 9.23872 7.40354 8.84039L7.44416 8.10859C7.4613 7.79932 7.65878 7.52955 7.94793 7.41777C9.23232 6.92145 10.6039 6.61553 11.9814 6.61553C13.3702 6.61553 14.7533 6.92629 16.047 7.42969C16.335 7.54185 16.5318 7.81124 16.5489 8.11977L16.5891 8.84002C16.6123 9.25697 16.9908 9.54612 17.3836 9.46974C18.3654 9.48091 18.937 10.0518 18.861 11.0477C18.8084 11.7352 18.5237 12.3947 17.7729 12.4853C17.8374 12.1298 17.3802 11.8988 17.1347 12.1667C16.936 12.3832 16.9066 12.8843 16.8019 13.1616C16.566 13.7846 16.2206 14.3651 15.7795 14.8644C14.9582 15.7944 13.8139 16.4096 12.5821 16.5523C12.4658 16.5657 12.3626 16.4741 12.3582 16.3571C12.3574 16.3396 12.357 16.3217 12.3563 16.3042C12.3526 16.1887 12.4167 16.0836 12.518 16.0284C12.8757 15.8343 13.1201 15.4583 13.1201 15.0235V14.1218C13.1201 14.0223 13.1701 13.9288 13.2547 13.8759C13.4316 13.7644 13.6463 13.6039 13.7025 13.4339C13.7618 13.2551 13.6351 13.0584 13.4391 13.0584H10.561C10.3647 13.0584 10.238 13.2551 10.2976 13.4339C10.3498 13.5912 10.5346 13.7361 10.6937 13.8416C10.7749 13.8952 10.823 13.9865 10.823 14.0842V15.0239C10.823 15.4587 11.0674 15.8347 11.4251 16.0288C11.5265 16.084 11.5906 16.189 11.5868 16.3045C11.5865 16.3198 11.5857 16.3355 11.5854 16.3507C11.5813 16.4685 11.4762 16.5605 11.3592 16.5456C10.1519 16.3928 9.08216 15.8231 8.28514 14.9605C7.77317 14.3673 7.37224 13.7261 7.12818 12.9786C7.0313 12.682 7.02646 12.0408 6.72912 11.8783C6.58045 11.7971 6.39787 11.8295 6.2831 11.9532C6.13778 12.1104 6.19293 12.2841 6.23094 12.4722C5.5744 12.3601 5.21967 11.8921 5.1526 11.0477C5.21967 11.8921 5.12354 10.6889 5.1526 11.0477ZM7.57718 22.1091C6.46009 21.5833 6.20634 19.9401 6.93256 18.9914C6.98622 18.9214 7.06 18.8685 7.14011 18.8316C7.313 18.7526 7.48142 18.6803 7.64164 18.6155C7.90992 18.507 8.22105 18.6151 8.35817 18.8703C8.7524 19.6036 9.36012 20.198 10.0856 20.5829C10.4344 20.7677 10.5189 21.2375 10.2328 21.5095C9.57847 22.1307 8.40512 22.4985 7.57718 22.1091ZM11.74 20.1208C11.7959 19.9394 11.8477 19.7542 11.895 19.566C11.9151 19.4855 12.0269 19.4855 12.047 19.566C12.0947 19.7549 12.1469 19.9408 12.2028 20.1231C12.2322 20.2192 12.1622 20.3153 12.0616 20.3146C12.0019 20.3142 11.9375 20.3135 11.8775 20.312C11.7787 20.3101 11.7109 20.2151 11.74 20.1208ZM17.3877 20.5806C17.3027 21.5833 16.5098 22.2779 15.5149 22.3043C14.7913 22.3233 14.1608 22.0271 13.71 21.5256C13.4518 21.2379 13.5434 20.7904 13.8881 20.6153C14.637 20.2348 15.266 19.6331 15.6722 18.8849C15.8123 18.627 16.123 18.5145 16.3946 18.6255C16.5109 18.6732 16.6313 18.7246 16.7542 18.7794C16.8284 18.8126 16.8977 18.8599 16.9506 18.9218C17.3176 19.3506 17.4469 19.8827 17.3877 20.5806C17.4514 19.8324 17.3128 21.4637 17.3877 20.5806Z" fill="#344054" />
                <path d="M9.10355 9.25782C8.1437 9.25372 8.13588 10.7442 9.09684 10.7483C10.0571 10.7524 10.0645 9.26192 9.10355 9.25782Z" fill="#344054" />
                <path d="M14.8936 9.27979C13.9337 9.27569 13.9259 10.7661 14.8869 10.7702C15.8471 10.7743 15.8546 9.28389 14.8936 9.27979Z" fill="#344054" />
                <path d="M13.7203 8.64122H15.8952C16.3748 8.64122 16.3755 7.896 15.8952 7.896H13.7203C13.2407 7.896 13.24 8.64122 13.7203 8.64122Z" fill="#344054" />
                <path d="M8.49793 8.64122L10.6486 8.65053C11.1878 8.65277 11.6249 9.09357 11.6226 9.63311L11.6174 10.944C11.6167 11.1072 11.4844 11.2391 11.3212 11.2391H11.2798C10.8003 11.2391 10.7996 11.9843 11.2798 11.9843H11.9859H11.9874H12.6947C13.1742 11.9843 13.1749 11.2391 12.6947 11.2391H12.6585C12.4946 11.2391 12.3619 11.1057 12.3623 10.9417L12.3675 9.63609C12.3712 8.68593 11.601 7.90941 10.6516 7.90531L8.50129 7.896C8.02509 7.896 8.01428 8.63861 8.49793 8.64122Z" fill="#344054" />
              </svg>
            </span>
            <h5 className="mb-0 font-lato not-italic font-medium text-[16px] leading-[150%] text-[#101828]">
              {dialogTitle}
            </h5>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-8">
          {/* Show current coach in edit mode */}
          {isEditMode && currentCoach && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Current Coach</p>
                  <p className="text-base font-semibold text-gray-900">
                    {getCurrentCoachLabel()}
                  </p>
                </div>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" fill="#3B82F6" />
                </svg>
              </div>
            </div>
          )}

          <div className="flex items-center">
            <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
              {isEditMode ? 'Change to' : 'Assign Coach'}
            </label>
            <div className="w-3/4 pl-2">
              <Select
                options={coachList}
                value={selectedCoach}
                onChange={handleSelectChange}
                placeholder={isEditMode ? 'Select new coach' : 'Select coaches to assign'}
                className="w-full"
                isMulti={true}
                disabled={coachListLoading || assignCoachLoading}
              />
              {coachListLoading && (
                <p className="text-xs text-gray-500 mt-1">Loading coaches...</p>
              )}
              {assignCoachLoading && (
                <p className="text-xs text-blue-600 mt-1">Assigning coaches...</p>
              )}
              {assignCoachError && (
                <p className="text-xs text-red-600 mt-1">{assignCoachError}</p>
              )}
              {!coachListLoading && coachList.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">No coaches available</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 pb-6">
          <button
            className="w-full h-10 rounded-lg text-sm font-medium text-gray-700"
            style={{ backgroundColor: '#FFF4E5' }}
            onClick={handleClose}
            disabled={assignCoachLoading}
          >
            Cancel
          </button>
          <BtnPrimary
            className="w-full h-10 rounded-lg text-sm font-medium flex items-center justify-center"
            style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
            onClick={handleAssignCoach}
            disabled={isButtonDisabled}
          >
            {assignCoachLoading ? 'Assigning...' : buttonText}
          </BtnPrimary>
        </div>
      </div>
    </Dialog>
  );
};

export default AssignCoachDialog;